function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import cx from 'clsx';
import _ from 'lodash';
import PropTypes from 'prop-types';
import * as React from 'react';
import { childrenUtils, customPropTypes, getComponentType, getUnhandledProps, SUI, getKeyOnly, getKeyOrValueAndKey, getValueAndKey, getWidthProp } from '../lib';
import Button from './Button';

/**
 * Buttons can be grouped.
 */
var ButtonGroup = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var attached = props.attached,
    basic = props.basic,
    buttons = props.buttons,
    children = props.children,
    className = props.className,
    color = props.color,
    compact = props.compact,
    content = props.content,
    floated = props.floated,
    fluid = props.fluid,
    icon = props.icon,
    inverted = props.inverted,
    labeled = props.labeled,
    negative = props.negative,
    positive = props.positive,
    primary = props.primary,
    secondary = props.secondary,
    size = props.size,
    toggle = props.toggle,
    vertical = props.vertical,
    widths = props.widths;
  var classes = cx('ui', color, size, getKeyOnly(basic, 'basic'), getKeyOnly(compact, 'compact'), getKeyOnly(fluid, 'fluid'), getKeyOnly(icon, 'icon'), getKeyOnly(inverted, 'inverted'), getKeyOnly(labeled, 'labeled'), getKeyOnly(negative, 'negative'), getKeyOnly(positive, 'positive'), getKeyOnly(primary, 'primary'), getKeyOnly(secondary, 'secondary'), getKeyOnly(toggle, 'toggle'), getKeyOnly(vertical, 'vertical'), getKeyOrValueAndKey(attached, 'attached'), getValueAndKey(floated, 'floated'), getWidthProp(widths), 'buttons', className);
  var rest = getUnhandledProps(ButtonGroup, props);
  var ElementType = getComponentType(props);
  if (_.isNil(buttons)) {
    return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
      className: classes,
      ref: ref
    }), childrenUtils.isNil(children) ? content : children);
  }
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes,
    ref: ref
  }), _.map(buttons, function (button) {
    return Button.create(button);
  }));
});
ButtonGroup.displayName = 'ButtonGroup';
ButtonGroup.propTypes = {
  /** An element type to render as (string or function). */
  as: PropTypes.elementType,
  /** Groups can be attached to other content. */
  attached: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['left', 'right', 'top', 'bottom'])]),
  /** Groups can be less pronounced. */
  basic: PropTypes.bool,
  /** Array of shorthand Button values. */
  buttons: customPropTypes.collectionShorthand,
  /** Primary content. */
  children: PropTypes.node,
  /** Additional classes. */
  className: PropTypes.string,
  /** Groups can have a shared color. */
  color: PropTypes.oneOf(SUI.COLORS),
  /** Groups can reduce their padding to fit into tighter spaces. */
  compact: PropTypes.bool,
  /** Shorthand for primary content. */
  content: customPropTypes.contentShorthand,
  /** Groups can be aligned to the left or right of its container. */
  floated: PropTypes.oneOf(SUI.FLOATS),
  /** Groups can take the width of their container. */
  fluid: PropTypes.bool,
  /** Groups can be formatted as icons. */
  icon: PropTypes.bool,
  /** Groups can be formatted to appear on dark backgrounds. */
  inverted: PropTypes.bool,
  /** Groups can be formatted as labeled icon buttons. */
  labeled: PropTypes.bool,
  /** Groups can hint towards a negative consequence. */
  negative: PropTypes.bool,
  /** Groups can hint towards a positive consequence. */
  positive: PropTypes.bool,
  /** Groups can be formatted to show different levels of emphasis. */
  primary: PropTypes.bool,
  /** Groups can be formatted to show different levels of emphasis. */
  secondary: PropTypes.bool,
  /** Groups can have different sizes. */
  size: PropTypes.oneOf(SUI.SIZES),
  /** Groups can be formatted to toggle on and off. */
  toggle: PropTypes.bool,
  /** Groups can be formatted to appear vertically. */
  vertical: PropTypes.bool,
  /** Groups can have their widths divided evenly. */
  widths: PropTypes.oneOf(SUI.WIDTHS)
};
export default ButtonGroup;
//# sourceMappingURL=ButtonGroup.js.map